import React, { useEffect, useRef } from 'react';

/**
 * Hero visual — citation-integrity flow on a paper ground.
 *
 * Reads left-to-right like a journal figure: a SOURCE manuscript with
 * citation markers in the prose; resolution lines from each marker into
 * an integrity-check apparatus that consults the resolved REFERENCE
 * documents fanned above it; output on the right is an integrity score
 * with the audit trail running underneath.
 *
 * Palette is tuned for the cream/ink theme — dark navy strokes, ink
 * type, a single muted-forest accent for the verified score.
 */
function HeroVisual() {
    const svgRef = useRef(null);

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        const nodes = svg.querySelectorAll('.hero-node');
        const paths = svg.querySelectorAll('.hero-path');

        nodes.forEach((node, i) => {
            node.style.animationDelay = `${i * 0.10}s`;
        });
        paths.forEach((path, i) => {
            path.style.animationDelay = `${i * 0.16 + 0.35}s`;
        });
    }, []);

    return (
        <div className="hero-visual-container">
            <svg
                ref={svgRef}
                viewBox="0 0 600 220"
                className="hero-visual-svg"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="A source manuscript's citations are traced to reference documents and rolled up into an integrity score."
            >
                <defs>
                    <linearGradient id="rfPathGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(30, 58, 95, 0.55)" />
                        <stop offset="100%" stopColor="rgba(47, 107, 74, 0.55)" />
                    </linearGradient>
                </defs>

                {/* SOURCE manuscript (left) */}
                <rect className="hero-node" x="40" y="35" width="135" height="155" rx="3"
                      fill="#fbf8f1" stroke="rgba(30, 58, 95, 0.45)" strokeWidth="1.1" />
                <text x="107" y="22" textAnchor="middle" fill="rgba(30, 58, 95, 0.7)"
                      fontSize="9" fontWeight="700" letterSpacing="2"
                      fontFamily="Inter, sans-serif">SOURCE</text>

                {/* manuscript text lines */}
                <line x1="55" y1="56" x2="160" y2="56" stroke="rgba(28, 31, 38, 0.28)" strokeWidth="1" />
                <line x1="55" y1="66" x2="150" y2="66" stroke="rgba(28, 31, 38, 0.22)" strokeWidth="1" />
                <line x1="55" y1="76" x2="155" y2="76" stroke="rgba(28, 31, 38, 0.22)" strokeWidth="1" />
                {/* citation marker 1 */}
                <circle className="hero-node" cx="153" cy="76" r="3.5" fill="#1e3a5f" />

                <line x1="55" y1="96" x2="160" y2="96" stroke="rgba(28, 31, 38, 0.28)" strokeWidth="1" />
                <line x1="55" y1="106" x2="145" y2="106" stroke="rgba(28, 31, 38, 0.22)" strokeWidth="1" />
                {/* citation marker 2 */}
                <circle className="hero-node" cx="138" cy="106" r="3.5" fill="#1e3a5f" />

                <line x1="55" y1="126" x2="160" y2="126" stroke="rgba(28, 31, 38, 0.22)" strokeWidth="1" />
                <line x1="55" y1="136" x2="153" y2="136" stroke="rgba(28, 31, 38, 0.22)" strokeWidth="1" />
                {/* citation marker 3 */}
                <circle className="hero-node" cx="148" cy="136" r="3.5" fill="#1e3a5f" />

                <line x1="55" y1="156" x2="125" y2="156" stroke="rgba(28, 31, 38, 0.22)" strokeWidth="1" />
                <line x1="55" y1="166" x2="140" y2="166" stroke="rgba(28, 31, 38, 0.22)" strokeWidth="1" />
                <line x1="55" y1="176" x2="135" y2="176" stroke="rgba(28, 31, 38, 0.18)" strokeWidth="1" />

                {/* trace lines from citations to integrity check */}
                <path className="hero-path" d="M 157 76 Q 220 76 280 95"
                      stroke="url(#rfPathGrad)" strokeWidth="1.4" fill="none" opacity="0.85" />
                <path className="hero-path" d="M 142 106 Q 210 106 280 110"
                      stroke="url(#rfPathGrad)" strokeWidth="1.4" fill="none" opacity="0.85" />
                <path className="hero-path" d="M 152 136 Q 220 130 280 125"
                      stroke="url(#rfPathGrad)" strokeWidth="1.4" fill="none" opacity="0.85" />

                {/* Integrity check apparatus (center) */}
                <rect className="hero-node gate-node" x="280" y="80" width="84" height="60" rx="4"
                      fill="#fbf8f1" stroke="rgba(30, 58, 95, 0.55)" strokeWidth="1.2" />
                <text x="322" y="103" textAnchor="middle" fill="#1e3a5f"
                      fontSize="9" fontWeight="700" letterSpacing="2"
                      fontFamily="Inter, sans-serif">INTEGRITY</text>
                <text x="322" y="116" textAnchor="middle" fill="rgba(30, 58, 95, 0.7)"
                      fontSize="8" fontWeight="500" letterSpacing="1"
                      fontFamily="Inter, sans-serif">CHECK</text>
                {/* check marks inside */}
                <path className="hero-node" d="M 296 130 l 4 4 l 7 -8" stroke="#2f6b4a"
                      strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                <path className="hero-node" d="M 326 130 l 4 4 l 7 -8" stroke="#2f6b4a"
                      strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />

                {/* REFERENCE documents (above gate, fanned) */}
                <rect className="hero-node" x="270" y="32" width="38" height="34" rx="2"
                      fill="#fbf8f1" stroke="rgba(28, 31, 38, 0.35)" strokeWidth="0.9" transform="rotate(-7 289 49)" />
                <rect className="hero-node" x="297" y="30" width="38" height="34" rx="2"
                      fill="#fbf8f1" stroke="rgba(28, 31, 38, 0.45)" strokeWidth="1" />
                <rect className="hero-node" x="328" y="32" width="38" height="34" rx="2"
                      fill="#fbf8f1" stroke="rgba(28, 31, 38, 0.35)" strokeWidth="0.9" transform="rotate(7 347 49)" />
                <text x="320" y="22" textAnchor="middle" fill="rgba(30, 58, 95, 0.7)"
                      fontSize="9" fontWeight="700" letterSpacing="2"
                      fontFamily="Inter, sans-serif">REFERENCES</text>

                {/* tiny lines on the reference docs */}
                <line x1="278" y1="48" x2="298" y2="48" stroke="rgba(28, 31, 38, 0.25)" strokeWidth="0.7" transform="rotate(-7 289 49)" />
                <line x1="278" y1="55" x2="295" y2="55" stroke="rgba(28, 31, 38, 0.18)" strokeWidth="0.7" transform="rotate(-7 289 49)" />
                <line x1="304" y1="44" x2="328" y2="44" stroke="rgba(28, 31, 38, 0.25)" strokeWidth="0.7" />
                <line x1="304" y1="52" x2="324" y2="52" stroke="rgba(28, 31, 38, 0.18)" strokeWidth="0.7" />
                <line x1="335" y1="48" x2="358" y2="48" stroke="rgba(28, 31, 38, 0.25)" strokeWidth="0.7" transform="rotate(7 347 49)" />

                {/* dotted connectors refs → check */}
                <line x1="289" y1="68" x2="305" y2="80" stroke="rgba(30, 58, 95, 0.35)" strokeWidth="0.8" strokeDasharray="2 2" />
                <line x1="316" y1="64" x2="322" y2="80" stroke="rgba(30, 58, 95, 0.35)" strokeWidth="0.8" strokeDasharray="2 2" />
                <line x1="347" y1="68" x2="338" y2="80" stroke="rgba(30, 58, 95, 0.35)" strokeWidth="0.8" strokeDasharray="2 2" />

                {/* path: check → score */}
                <path className="hero-path" d="M 364 110 Q 410 110 450 110"
                      stroke="url(#rfPathGrad)" strokeWidth="2" fill="none" opacity="0.8" />
                <circle r="2.5" fill="#2f6b4a">
                    <animateMotion dur="3.2s" repeatCount="indefinite" path="M 364 110 Q 410 110 450 110" />
                </circle>

                {/* Integrity score badge (right) */}
                <g className="score-pulse">
                    <circle cx="510" cy="110" r="48" fill="#fbf8f1"
                            stroke="#2f6b4a" strokeWidth="1.6" />
                    <text x="510" y="106" textAnchor="middle" fill="#1c1f26"
                          fontSize="26" fontWeight="600" letterSpacing="-0.02em"
                          fontFamily="'Source Serif 4', Georgia, serif">92</text>
                    <text x="510" y="125" textAnchor="middle" fill="#2f6b4a"
                          fontSize="7.5" fontWeight="700" letterSpacing="2"
                          fontFamily="Inter, sans-serif">INTEGRITY</text>
                </g>

                {/* audit trail */}
                <path className="hero-path ledger-path"
                      d="M 107 200 L 322 200 L 510 200"
                      stroke="rgba(28, 31, 38, 0.18)" strokeWidth="0.9" fill="none" strokeDasharray="3 4" />
                <text x="307" y="214" textAnchor="middle" fill="rgba(28, 31, 38, 0.42)"
                      fontSize="8" fontWeight="600" letterSpacing="2"
                      fontFamily="Inter, sans-serif">AUDIT TRAIL</text>
            </svg>
        </div>
    );
}

export default HeroVisual;
